import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppBooking from '../components/WhatsAppBooking';
import SEOJsonLd from '../../../components/base/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thesunshinehealing.com';

const studioImage = 'https://public.readdy.ai/ai/img_res/561a5639c2d8c3dc8e54df329358952e.jpg spiritual healing studio banner design with glowing lotus flowers chakra rainbow energy light sacred geometry golden patterns serene meditation space white and gold cream color palette peaceful divine atmosphere ethereal glow natural elements Singapore wellness brand beautiful harmonious spiritual landscape with soft light rays and healing crystals&width=1200&height=630&seq=studio-og-preview-001&orientation=landscape';

const pellowahJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'PELLOWAH Energy Healing Singapore — High-Frequency Consciousness Expansion',
    description:
      'PELLOWAH high-frequency energy healing in Singapore. Direct connection to Higher Self, consciousness expansion, break through limiting beliefs. In-person and remote sessions. 60–90 mins from SGD $150.',
    url: `${siteUrl}/services/pellowah`,
    image: studioImage,
    areaServed: 'Singapore',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Sunshine Healing Studio',
      url: siteUrl,
      telephone: '+6593651286',
      address: { '@type': 'PostalAddress', addressLocality: 'Singapore', addressCountry: 'SG' },
    },
    offers: [
      { '@type': 'Offer', price: '150', priceCurrency: 'SGD', description: 'Standard PELLOWAH Session (60 mins)' },
      { '@type': 'Offer', price: '200', priceCurrency: 'SGD', description: 'Deep PELLOWAH Session (90 mins)' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页 Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'PELLOWAH 能量疗愈', item: `${siteUrl}/services/pellowah` },
    ],
  },
];

export default function PellowahPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-white">
      <title>PELLOWAH 能量疗愈 新加坡 | Pellowah Energy Healing Singapore — Consciousness Expansion</title>
      <meta name="description" content="新加坡PELLOWAH高频能量疗愈，直接连接高我，扩展意识维度，突破内在限制信念，提升灵性觉知。可面对面或远程疗愈，60-90分钟，SGD $150起。Singapore PELLOWAH energy healing — Higher Self connection, consciousness expansion." />
      <meta name="keywords" content="PELLOWAH Singapore, energy healing Singapore, consciousness expansion, higher self healing, spiritual healing Singapore" />
      <link rel="canonical" href={`${siteUrl}/services/pellowah`} />
      <meta property="og:title" content="PELLOWAH 能量疗愈 新加坡 | Pellowah Healing Singapore" />
      <meta property="og:description" content="PELLOWAH高频能量疗愈，连接高我，扩展意识，突破限制。新加坡专业能量疗愈师Susie，可远程或面对面疗愈。" />
      <meta property="og:url" content={`${siteUrl}/services/pellowah`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={studioImage} />
      <SEOJsonLd id="pellowah-jsonld" data={pellowahJsonLd} />

      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=ethereal%20golden%20light%20energy%20flowing%20through%20cosmic%20space%20with%20sacred%20geometry%20patterns%2C%20spiritual%20healing%20atmosphere%2C%20soft%20glowing%20particles%2C%20peaceful%20and%20transcendent%20ambiance%2C%20high%20frequency%20vibration%20visualization%2C%20mystical%20divine%20energy%20field%2C%20serene%20gradient%20background&width=1920&height=1080&seq=pellowah-hero-bg&orientation=landscape"
            alt="PELLOWAH Energy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/60 via-stone-900/40 to-stone-900/60"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-400/30 mb-4 md:mb-5">
            <span className="text-amber-300 text-xs md:text-sm font-medium tracking-widest">SUNSHINE HEALING</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-4 md:mb-5 tracking-tight">PELLOWAH</h1>
          <p className="text-base md:text-xl text-stone-200 leading-relaxed">
            {t('pellowahDetail.hero.description')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 md:items-center gap-8">
            <div>
              <p className="text-amber-600 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">Introduction</p>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4 md:mb-6 leading-snug">
                {t('pellowahDetail.intro.title')}
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm md:text-base mb-5">
                {t('pellowahDetail.intro.content')}
              </p>

              {/* Core Features */}
              <div className="mb-5">
                <p className="text-stone-800 font-semibold text-sm md:text-base mb-3">
                  {t('pellowahDetail.intro.featuresTitle')}
                </p>
                <ul className="space-y-3">
                  {(t('pellowahDetail.intro.features', { returnObjects: true }) as { title: string; desc: string }[]).map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                      </div>
                      <div>
                        <span className="font-semibold text-stone-800 text-sm md:text-base">{feature.title}：</span>
                        <span className="text-stone-600 text-sm md:text-base">{feature.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-stone-500 text-sm md:text-base italic border-l-2 border-amber-300 pl-4">
                {t('pellowahDetail.intro.conclusion')}
              </p>

              <div className="mt-6 md:mt-8 grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { icon: 'ri-sparkling-2-line', label: t('pellowahDetail.benefits.benefit1.title') },
                  { icon: 'ri-mental-health-line', label: t('pellowahDetail.benefits.benefit2.title') },
                  { icon: 'ri-focus-3-line', label: t('pellowahDetail.benefits.benefit3.title') },
                  { icon: 'ri-heart-line', label: t('pellowahDetail.benefits.benefit4.title') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:gap-3 bg-amber-50 rounded-xl px-3 md:px-4 py-2.5 md:py-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-amber-600 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-lg`}></i>
                    </div>
                    <span className="text-stone-700 text-xs md:text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full h-64 md:h-[520px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=person%20meditating%20in%20lotus%20position%20surrounded%20by%20golden%20light%20energy%20waves%2C%20spiritual%20awakening%20scene%2C%20peaceful%20healing%20environment%2C%20soft%20ethereal%20glow%2C%20sacred%20space%20with%20natural%20elements%2C%20serene%20and%20uplifting%20atmosphere%2C%20high%20vibration%20energy%20field%20visualization&width=800&height=1000&seq=pellowah-intro-img&orientation=portrait"
                alt="PELLOWAH Healing"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Suitable For */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-amber-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10">
            {/* 适合人群 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('pellowahDetail.suitable.title')}</h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('pellowahDetail.suitable.item1'),
                  t('pellowahDetail.suitable.item2'),
                  t('pellowahDetail.suitable.item3'),
                  t('pellowahDetail.suitable.item4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-600 text-sm md:text-base">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* 疗愈方式 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-magic-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('pellowahDetail.method.title')}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-hand-heart-line', title: t('pellowahDetail.method.step1.title'), desc: t('pellowahDetail.method.step1.desc') },
                  { icon: 'ri-flashlight-line', title: t('pellowahDetail.method.step2.title'), desc: t('pellowahDetail.method.step2.desc') },
                  { icon: 'ri-eye-2-line', title: t('pellowahDetail.method.step3.title'), desc: t('pellowahDetail.method.step3.desc') },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-amber-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm md:text-base mb-1">{item.title}</p>
                      <p className="text-stone-500 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('pellowahDetail.pricing.title')}</h3>
          </div>
          <p className="text-stone-400 text-sm mb-8">{t('pellowahDetail.pricing.note')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {(t('pellowahDetail.pricing.items', { returnObjects: true }) as { duration: string; price: string; label: string }[]).map((item, i) => (
              <div key={i} className="flex-1 max-w-xs mx-auto sm:mx-0 bg-amber-50 border border-amber-100 rounded-2xl px-8 py-7 flex flex-col items-center gap-2 shadow-sm">
                <span className="text-xs font-semibold tracking-widest text-amber-500 uppercase mb-1">{item.label}</span>
                <div className="flex items-center gap-2 text-stone-700">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-400">
                    <i className="ri-time-line text-base"></i>
                  </div>
                  <span className="text-base font-medium">{item.duration}</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-amber-600 mt-1">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <WhatsAppBooking accentColor="amber" serviceName="PELLOWAH" />

      {/* Back */}
      <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-white">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 px-6 md:px-7 py-3 border border-stone-300 text-stone-700 rounded-full hover:bg-stone-100 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t('pellowahDetail.cta.back')}</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
